import React from 'react';
import { motion } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { MessageSquare, Trash2, Target } from 'lucide-react';
import { CopilotThread } from '@/hooks/ai-agents/types/database-types';
import { cn } from '@/lib/utils';
import { format, isToday, isYesterday } from 'date-fns';
import AssistantAvatarGroup from './AssistantAvatarGroup';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ThreadItemProps {
  thread: CopilotThread;
  isActive?: boolean;
  onClick: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
}

// Hook to fetch project information for threads with project context
const useThreadProjectInfo = (thread: CopilotThread) => {
  return useQuery({
    queryKey: ['thread-project-info', thread.id],
    queryFn: async () => {
      // Check if this thread has project context
      const hasProjectContext = thread.metadata?.projectContext && thread.metadata?.goalId;
      if (!hasProjectContext) return null;

      // Get project info via the junction table
      const { data, error } = await supabase
        .from('planner_goals_workbooks')
        .select(`
          workbook:workbook_id (
            id,
            title
          )
        `)
        .eq('goal_id', thread.metadata.goalId)
        .eq('agent_thread_id', thread.id)
        .single();

      if (error || !data?.workbook) return null;

      return {
        projectId: data.workbook.id,
        projectTitle: data.workbook.title
      };
    },
    enabled: !!(thread.metadata?.projectContext && thread.metadata?.goalId),
    staleTime: 5 * 60 * 1000, // 5 minutes - project info doesn't change often
  });
};

const ThreadItem: React.FC<ThreadItemProps> = ({
  thread,
  isActive = false,
  onClick,
  onDelete,
  isDeleting = false
}) => {
  // Get project information for this thread
  const { data: projectInfo } = useThreadProjectInfo(thread);

  const formatDate = (dateStr: string | number) => {
    const date = typeof dateStr === 'number' ? new Date(dateStr) : new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMM d');
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  // Truncate the last message effectively
  const truncateMessage = (message: string, maxLength: number = 50) => {
    if (message.length <= maxLength) return message;
    
    // Find last complete word within the limit
    const truncated = message.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.7) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
  };

  const displayMessage = thread.lastMsgFromUser 
    ? truncateMessage(thread.lastMsgFromUser)
    : 'New conversation';

  return (
    <motion.div
      className={cn(
        "group relative flex items-start gap-3 p-3 rounded-md cursor-pointer transition-all duration-300",
        "hover:border-blue-200/50 dark:hover:border-blue-800/50 border border-transparent",
        isActive && "border-blue-300/70 dark:border-blue-700/70"
      )}
      onClick={onClick}
      whileHover={CHAT_ANIMATIONS.threadItem.hover}
      whileTap={CHAT_ANIMATIONS.threadItem.tap}
    >
      {/* Simplified background with glass effect */}
      <div className={cn(
        "absolute inset-0 backdrop-blur-sm border border-white/20 dark:border-white/5 rounded-md transition-all duration-300",
        isActive
          ? "bg-gradient-to-br from-blue-100/60 to-purple-100/50 dark:from-blue-900/20 dark:to-purple-900/15 opacity-90"
          : "bg-gradient-to-br from-white/50 to-white/30 dark:from-slate-800/40 dark:to-slate-900/30 opacity-0 group-hover:opacity-80"
      )} />
      
      {/* Active state highlight */}
      {isActive && (
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 dark:from-blue-400/5 dark:to-purple-400/5 rounded-md" />
      )}
      
      {/* Content */}
      <div className="relative z-10 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <MessageSquare 
              size={14} 
              className={cn(
                "flex-shrink-0 mt-0.5",
                isActive 
                  ? "text-blue-600 dark:text-blue-400" 
                  : "text-slate-500 dark:text-slate-400"
              )} 
            />
            <div className="flex-1 min-w-0">
              {/* Last user message */}
              <p className={cn(
                "text-sm font-medium truncate",
                isActive 
                  ? "text-blue-900 dark:text-blue-100" 
                  : "text-slate-700 dark:text-slate-200"
              )}>
                {displayMessage}
              </p>
            </div>
          </div>
          
          {/* Delete button */}
          <motion.button
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className={cn(
              "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
              "p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30",
              "text-slate-400 hover:text-red-600 dark:hover:text-red-400",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {isDeleting ? (
              <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Trash2 size={12} />
            )}
          </motion.button>
        </div>
        
        {/* Metadata: Date, Project, and Assistant Avatars */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-3">
            <span className={cn(
              "text-xs",
              isActive 
                ? "text-blue-600/70 dark:text-blue-300/70" 
                : "text-slate-500 dark:text-slate-400"
            )}>
              {formatDate((thread as any).lastActivity || thread.updatedAt || thread.createdAt)}
            </span>
            
            {/* Message count */}
            {(thread as any).messageCount > 0 && (
              <span className={cn(
                "text-xs px-1.5 py-0.5 rounded-full",
                isActive 
                  ? "bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300" 
                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
              )}>
                {(thread as any).messageCount}
              </span>
            )}
            
            {/* 🎯 Beautiful Project Context Display - Same design as TaskCardDates */}
            {projectInfo && (
              <div className="flex items-center">
                <Target 
                  size={12} 
                  className={cn(
                    isActive 
                      ? "text-purple-600 dark:text-purple-400" 
                      : "text-purple-500 dark:text-purple-400"
                  )} 
                />
                <span 
                  className={cn(
                    "ml-1 text-xs font-medium bg-clip-text text-transparent",
                    isActive 
                      ? "bg-gradient-to-r from-purple-600 to-blue-700 dark:from-purple-400 dark:to-blue-500" 
                      : "bg-gradient-to-r from-purple-500 to-blue-600 dark:from-purple-400 dark:to-blue-500"
                  )}
                >
                  Project: {projectInfo.projectTitle}
                </span>
              </div>
            )}
          </div>
          
          {/* Assistant Avatars */}
          {thread.assistants && thread.assistants.length > 0 && (
            <AssistantAvatarGroup 
              assistants={thread.assistants} 
              maxVisible={3}
              size="sm"
            />
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ThreadItem;
